/**
 * Gift-shop MultiSafepay diagnostics. Search Cloud Logging for `GIFT_MSP_CLIENT`.
 * Also prints to the browser console. Card payloads and API tokens are stripped.
 */
import { getApiBaseUrl } from '../api/config';

export const GIFT_MSP_LOG = 'GIFT_MSP_CLIENT';

const REDACT = /^(payload|api_token|apiToken|card_number|cardnumber|pan|cvc|cvv)$/i;

function redact(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[truncated]';
  if (value == null) return value;
  if (typeof value === 'string') return value.length > 800 ? `${value.slice(0, 800)}…[len=${value.length}]` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => redact(item, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      if (REDACT.test(key)) {
        const raw = typeof nested === 'string' ? nested : nested == null ? '' : JSON.stringify(nested);
        out[key] = `[redacted len=${raw.length}]`;
      } else {
        out[key] = redact(nested, depth + 1);
      }
    }
    return out;
  }
  return String(value);
}

function snapshot(fields?: Record<string, unknown>): Record<string, unknown> {
  return {
    href: typeof window !== 'undefined' ? window.location.href : null,
    ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    ...((redact(fields || {}) as Record<string, unknown>) || {}),
  };
}

/** Console + Cloud Logging (POST /gift-vouchers/client-log). Never throws. */
export function giftMspLog(event: string, fields?: Record<string, unknown>): void {
  const payload = { event, t: Date.now(), ...snapshot(fields) };
  try {
    // warn: gift prod used to drop console.info; warn stays visible in DevTools.
    // eslint-disable-next-line no-console
    console.warn(GIFT_MSP_LOG, event, payload);
  } catch {
    /* ignore */
  }

  try {
    const body = JSON.stringify({
      diagnostic_id: GIFT_MSP_LOG,
      event,
      ...payload,
    });
    void fetch(`${getApiBaseUrl()}/gift-vouchers/client-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* ignore */
  }
}
